import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { API_BASE_URL } from '../config/api';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import SpinTaskerLogo from '../components/shared/SpinTaskerLogo';
import { Download, Menu, X, Play, Loader2, Lock } from 'lucide-react';
import { submitTutorialComment } from '../redux/api';
import { loadTutorialsManifest, isPublicTutorialUnlocked } from '../utils/tutorialsManifest';
import { trackTutorialEvent, getVisitorEmail } from '../utils/tutorialAnalytics';
import { usePageTracking } from '../hooks/usePageTracking';
import { getMarketingHomeUrl } from '../utils/siteHost';
import { isAuthSessionValid } from '../utils/tokenUtils';
import {
  trackCtaClick,
  trackFormSubmit,
  trackFormSuccess,
  trackFormError
} from '../utils/googleAnalytics';

const Tutorials = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isInstallInstructionsOpen, setIsInstallInstructionsOpen] = useState(false);
  const [tutorials, setTutorials] = useState([]);
  const [listState, setListState] = useState('loading');
  const videoRef = useRef(null);
  const [listError, setListError] = useState(null);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [videoSrc, setVideoSrc] = useState('');
  const [formData, setFormData] = useState({
    companyEmail: '',
    tutorialId: '',
    tutorialTitle: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitInfo, setSubmitInfo] = useState('');
  const [approvedComments, setApprovedComments] = useState([]);
  const pageViewOnce = useRef(false);
  const isLoggedIn = isAuthSessionValid();

  usePageTracking('tutorials', { email: getVisitorEmail() || undefined });

  useEffect(() => {
    if (pageViewOnce.current) return;
    pageViewOnce.current = true;
    trackTutorialEvent({
      event: 'page_view',
      source: 'public',
      tutorialId: 'page_tutorials',
      path: typeof window !== 'undefined' ? window.location.pathname : '/tutorials'
    });
  }, []);

  const loadTutorials = async () => {
    try {
      setListState('loading');
      setListError(null);
      setSelectedTutorial(null);
      setTutorials([]);
      const list = await loadTutorialsManifest();
      setTutorials(list);
      if (list.length === 0) {
        setListError('empty');
        setListState('error');
      } else {
        setListState('ready');
      }
    } catch {
      setTutorials([]);
      setListError('loadFailed');
      setListState('error');
    }
  };

  const loadApprovedComments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tutorials/comments`, { cache: 'no-store' });
      if (!response.ok) return;
      const data = await response.json();
      setApprovedComments(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadApprovedComments();
  }, []);

  useEffect(() => {
    loadTutorials();
  }, []);

  useEffect(() => {
    if (!selectedTutorial?.videoUrl) return;
    setVideoSrc(selectedTutorial.videoUrl);
  }, [selectedTutorial?.id, selectedTutorial?.videoUrl]);

  useEffect(() => {
    if (!selectedTutorial?.videoUrl) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [selectedTutorial?.videoUrl]);

  const goToLogin = (location) => {
    trackCtaClick('login', { cta_location: location });
    navigate('/auth?role=company&type=login');
  };

  const playTutorial = (item, idx) => {
    if (!isPublicTutorialUnlocked(idx, isLoggedIn)) {
      goToLogin('tutorials_locked');
      return;
    }
    trackTutorialEvent({
      event: 'play',
      source: 'public',
      tutorialId: item.id || `idx-${idx}`,
      title: item.title || ''
    });
    trackCtaClick('tutorial_play', {
      cta_location: 'tutorials_list',
      tutorial_id: item.id || `idx-${idx}`,
      tutorial_title: item.title || ''
    });
    setSelectedTutorial(item);
    setVideoSrc(item.videoUrl || '');
  };

  const closeTutorialPlayer = () => {
    try {
      videoRef.current?.pause?.();
    } catch {
      // ignore
    }
    setSelectedTutorial(null);
    setVideoSrc('');
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem('profile');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const email = parsed?.result?.email;
      if (email && typeof email === 'string') {
        setFormData((prev) => ({ ...prev, companyEmail: email }));
      }
    } catch {
      // ignore
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitInfo('');
    setSubmitting(true);
    trackFormSubmit('tutorial_comment');
    try {
      await submitTutorialComment({
        companyEmail: formData.companyEmail.trim(),
        tutorialId: formData.tutorialId,
        tutorialTitle: formData.tutorialTitle,
        message: formData.message
      });
      trackFormSuccess('tutorial_comment');
      setSubmitInfo(t('tutorials.publicPage.submitSuccess'));
      setFormData((prev) => ({
        ...prev,
        tutorialId: '',
        tutorialTitle: '',
        message: ''
      }));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t('tutorials.publicPage.submitErrorFallback');
      trackFormError('tutorial_comment', { message: msg });
      setSubmitInfo(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-gray-900 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <SpinTaskerLogo />
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <LanguageSwitcher />
              <button
                onClick={() => {
                  trackCtaClick('login', { cta_location: 'tutorials_nav' });
                  navigate('/auth?role=company&type=login');
                }}
                className="text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {t('tutorials.publicPage.login')}
              </button>
              <button
                onClick={() => {
                  trackCtaClick('register', { cta_location: 'tutorials_nav' });
                  navigate('/auth?role=company&type=register');
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
              >
                {t('tutorials.publicPage.register')}
              </button>
              <button
                type="button"
                onClick={() => setIsInstallInstructionsOpen(true)}
                className="flex items-center gap-1.5 text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>{t('tutorials.publicPage.download')}</span>
              </button>
            </div>
            <button
              type="button"
              className="md:hidden text-gray-300 hover:text-white focus:outline-none"
              onClick={() => setMobileNavOpen((prev) => !prev)}
              aria-label={t('tutorials.publicPage.navMenuAria')}
            >
              {mobileNavOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {mobileNavOpen && (
            <div className="md:hidden absolute top-full left-0 w-full bg-gray-900 border-b border-gray-700 shadow-lg">
              <div className="px-4 py-4 space-y-3">
                <LanguageSwitcher />
                <button
                  onClick={() => {
                    trackCtaClick('login', { cta_location: 'tutorials_nav_mobile' });
                    navigate('/auth?role=company&type=login');
                    setMobileNavOpen(false);
                  }}
                  className="block w-full text-left text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {t('tutorials.publicPage.login')}
                </button>
                <button
                  onClick={() => {
                    trackCtaClick('register', { cta_location: 'tutorials_nav_mobile' });
                    navigate('/auth?role=company&type=register');
                    setMobileNavOpen(false);
                  }}
                  className="block w-full text-left bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  {t('tutorials.publicPage.register')}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-5xl mx-auto py-10 px-4">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">{t('tutorials.publicPage.pageTitle')}</h1>
          <a href={getMarketingHomeUrl()} className="text-blue-600 hover:text-blue-700 font-semibold">
            {t('tutorials.publicPage.backToHome')}
          </a>
        </div>

        <div className="bg-white rounded-xl shadow p-6 mb-8">
          {listState === 'loading' && (
            <div className="flex items-center gap-2 text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('tutorials.publicPage.loadingList')}
            </div>
          )}

          {listState === 'error' && listError && (
            <div className="space-y-3">
              <p className="text-red-600 text-sm">
                {listError === 'empty'
                  ? t('tutorials.publicPage.errorEmptyList')
                  : t('tutorials.publicPage.errorLoadFailed')}
              </p>
              <button
                type="button"
                onClick={loadTutorials}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
              >
                {t('tutorials.publicPage.retryLoad')}
              </button>
            </div>
          )}

          {listState === 'ready' && tutorials.length > 0 && (
            <div className="space-y-4">
              {!isLoggedIn ? (
                <p className="text-sm text-gray-600">
                  {t('tutorials.publicPage.listNote')}
                </p>
              ) : null}
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                {tutorials.map((item, idx) => {
                  const isActive = selectedTutorial?.id === item.id;
                  const unlocked = isPublicTutorialUnlocked(idx, isLoggedIn);
                  return (
                    <li
                      key={item.id || idx}
                      className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${
                        isActive ? 'bg-green-50' : unlocked ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-semibold ${unlocked ? 'text-gray-900' : 'text-gray-700'}`}>
                          {idx + 1}. {item.title || t('tutorials.publicPage.tutorialFallback', { n: idx + 1 })}
                        </h3>
                        {item.duration ? (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t('tutorials.durationLabel')}: {item.duration}
                          </p>
                        ) : null}
                        {!unlocked ? (
                          <p className="text-xs text-amber-700 mt-1 font-medium">
                            {t('tutorials.publicPage.lockedHint')}
                          </p>
                        ) : null}
                      </div>
                      {unlocked ? (
                        <button
                          type="button"
                          onClick={() => playTutorial(item, idx)}
                          className="inline-flex items-center justify-center gap-2 shrink-0 rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-gray-800"
                          aria-label={t('tutorials.publicPage.playAria', {
                            title: item.title || item.id || String(idx + 1)
                          })}
                        >
                          <Play className="h-4 w-4" />
                          {t('tutorials.publicPage.play')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => goToLogin('tutorials_locked')}
                          className="inline-flex items-center justify-center gap-2 shrink-0 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2 text-sm font-medium hover:bg-amber-100"
                          aria-label={t('tutorials.publicPage.lockedAria', {
                            title: item.title || item.id || String(idx + 1)
                          })}
                        >
                          <Lock className="h-4 w-4" />
                          {t('tutorials.publicPage.lockedCta')}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

        </div>

        <div className="bg-white rounded-xl shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('tutorials.publicPage.commentsTitle')}</h2>
          <p className="text-sm text-gray-600 mb-3">
            <Trans
              i18nKey="tutorials.publicPage.commentsIntro"
              components={{
                highlight: <strong className="font-medium text-gray-800" />
              }}
            />
          </p>
          <form className="space-y-3" onSubmit={handleSubmit}>
            <input
              type="email"
              name="companyEmail"
              value={formData.companyEmail}
              onChange={handleChange}
              placeholder={t('tutorials.publicPage.placeholderCompanyEmail')}
              className="w-full rounded border border-gray-300 px-3 py-2"
              autoComplete="email"
            />
            <input
              type="text"
              name="tutorialTitle"
              value={formData.tutorialTitle}
              onChange={handleChange}
              placeholder={t('tutorials.publicPage.placeholderTutorialTitle')}
              className="w-full rounded border border-gray-300 px-3 py-2"
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t('tutorials.publicPage.placeholderMessage')}
              rows={4}
              className="w-full rounded border border-gray-300 px-3 py-2"
              required
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? t('tutorials.publicPage.submitting') : t('tutorials.publicPage.submit')}
            </button>
          </form>
          {submitInfo && <p className="mt-3 text-sm text-gray-700">{submitInfo}</p>}
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {t('tutorials.publicPage.publishedCommentsTitle')}
          </h3>
          {approvedComments.length === 0 ? (
            <p className="text-gray-600">{t('tutorials.publicPage.noPublishedComments')}</p>
          ) : (
            <div className="space-y-3">
              {approvedComments.map((comment) => (
                <div key={comment._id} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-900">{comment.message}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {comment.authorName} •{' '}
                    {new Date(comment.createdAt).toLocaleString(
                      i18n.language?.startsWith('en') ? 'en-GB' : 'sr-RS'
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedTutorial?.videoUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tutorial-player-public-title"
          onClick={closeTutorialPlayer}
        >
          <div
            className="relative w-full max-w-5xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl p-4 sm:p-6 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeTutorialPlayer}
              className="absolute right-3 top-3 z-10 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label={t('tutorials.closePlayer')}
            >
              <X className="h-5 w-5" />
            </button>
            <h3
              id="tutorial-player-public-title"
              className="text-lg sm:text-xl font-semibold text-gray-900 pr-12"
            >
              {(() => {
                const i = tutorials.findIndex((t) => (t.id || '') === (selectedTutorial.id || ''));
                const prefix = i >= 0 ? `${i + 1}. ` : '';
                return `${prefix}${selectedTutorial.title || selectedTutorial.id}`;
              })()}
            </h3>
            {selectedTutorial.shortDescription ? (
              <p className="text-sm text-gray-600 mt-2 mb-3 leading-relaxed">{selectedTutorial.shortDescription}</p>
            ) : null}
            <video
              ref={videoRef}
              key={videoSrc || selectedTutorial.videoUrl}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="w-full rounded-xl bg-black max-h-[min(72dvh,640px)] object-contain"
              src={videoSrc || selectedTutorial.videoUrl}
              onError={() => {
                if (selectedTutorial?.fallbackVideoUrl && videoSrc !== selectedTutorial.fallbackVideoUrl) {
                  setVideoSrc(selectedTutorial.fallbackVideoUrl);
                }
              }}
            >
              {t('tutorials.videoUnsupported')}
            </video>
          </div>
        </div>
      )}

      {isInstallInstructionsOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setIsInstallInstructionsOpen(false)} />
          <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('tutorials.publicPage.installModalTitle')}
              </h3>
              <button
                type="button"
                onClick={() => setIsInstallInstructionsOpen(false)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600">{t('tutorials.publicPage.installModalBody')}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default Tutorials;
